import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faFile, faImage } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useImageLibrary from '@hooks/useImageLibrary'
import { generateUrls as generateAssetLibraryUrls } from '@hooks/useAssetLibrary'

import useCurrentUser from '@hooks/useCurrentUser'
import useImageLibraries from '@hooks/useImageLibraries'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import ImageLibraryListItem from '../ImageLibraryListItem'

const buildPageFilterFields = (currentUserPayload: ReturnType<typeof useCurrentUser>) => {
  const { isBrandUser } = currentUserPayload

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      callbacks: {
        shouldRender: ({ brand_id }: { brand_id?: number }) => !brand_id,
      },
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      hidden: isBrandUser,
      key: 'owner_type',
      label: 'Filter by Owner Type',
      values: [
        { key: 'Agency', label: 'Agency' },
        { key: 'Brand', label: 'Brand' },
      ],
    },
    {
      callbacks: {
        shouldRender: ({ owner_type }: { owner_type?: string }) => owner_type === 'Brand',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      hidden: isBrandUser,
      key: 'owner_id',
    },
    {
      callbacks: {
        shouldRender: ({ owner_type }: { owner_type?: string }) => owner_type === 'Agency',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
      },
      key: 'owner_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  useLocation()

  const currentUserPayload = useCurrentUser()
  const { currentUser: { brand_id }, isBrandUser } = useCurrentUser()

  const imageLibrariesPayload = useImageLibraries({
    filters: {
      ...pageFilters,
    },
    performHttpRequests: true,
    requestOptions: {
      include_image_library_cover_image: true,
      include_image_library_image_count: true,
      include_image_library_owner: true,
      include_image_sizes: true,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredImageLibraries,
    filteredImageLibrariesCount,
    hasFilteredImageLibraries,
    loading,
  } = imageLibrariesPayload

  const initImageLibrary = isBrandUser ? { owner_id: brand_id, owner_type: 'Brand' } : {}
  const useImageLibraryPayload = useImageLibrary(initImageLibrary)

  const {
    callbacks: { createOrEditImageLibrary },
    urls: { imageLibrariesIndexUrl },
  } = useImageLibraryPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { assetLibrariesIndexUrl } = generateAssetLibraryUrls()

  return (
    <>
      <Helmet>
        <title>Image Libraries | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            onClick={() => createOrEditImageLibrary()}
            size="medium"
          >
            Create
          </Button>
        )}
        activeTabBarItemKey="imageLibraries"
        tabBarItems={[
          {
            href: imageLibrariesIndexUrl,
            icon: faImage,
            key: 'imageLibraries',
            title: 'Image Libraries',
          },
          {
            href: assetLibrariesIndexUrl,
            icon: faFile,
            key: 'assetLibraries',
            title: 'Document Libraries',
          },
        ]}
        title="Libraries"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredImageLibraries && <ListBlankState />}

            {!loading && hasFilteredImageLibraries && (
              <DashboardModule title="Image Libraries">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredImageLibraries.map(imageLibrary => (
                      <ImageLibraryListItem
                        imageLibrary={imageLibrary}
                        key={imageLibrary.id}
                      />
                    ))}

                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredImageLibrariesCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(currentUserPayload)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
